import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, CheckCircle2 } from 'lucide-react';
import HeroPlaceholder from '../components/HeroPlaceholder';
import MattingCalculator from '../components/MattingCalculator';
import SEO from '../components/SEO';
import { HERO_MEDIA } from '../config/media';
import { submitQuote } from '../lib/api';
import { usePageContent } from '../lib/useContent';
import { orgJsonLd, breadcrumbJsonLd } from '../lib/seo';

const QUOTE_HERO_FALLBACK = {
    hero: {
        eyebrow: 'Manufacturer Direct',
        headline: 'Get a Manufacturer Direct Quote.',
        body: 'No middlemen. Pricing, lead times and delivery from our Chemainus, BC mill direct to your site.',
        image_url: HERO_MEDIA.muskeg.src,
    },
};

const MAT_TYPES = [
    { value: 'access', label: 'Access Mats' },
    { value: 'rig', label: 'Rig Mats' },
    { value: 'crane', label: 'Crane Mats' },
    { value: 'mixed', label: 'Mixed' },
    { value: 'unknown', label: 'Not sure - advise me' },
];

const PROJECT_TYPES = [
    { value: 'pipeline', label: 'Pipeline' },
    { value: 'drilling', label: 'Drilling' },
    { value: 'crane_lift', label: 'Crane Lift / Heavy Lift' },
    { value: 'access_road', label: 'Access Road' },
    { value: 'wind_solar', label: 'Wind / Solar' },
    { value: 'other', label: 'Other' },
];

const PROVINCES = [
    { value: 'BC', label: 'British Columbia' },
    { value: 'AB', label: 'Alberta' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'YT', label: 'Yukon' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'OTHER', label: 'Other' },
];

export default function Quote() {
    const content = usePageContent('quote', QUOTE_HERO_FALLBACK);
    const hero = content.hero || QUOTE_HERO_FALLBACK.hero;
    const location = useLocation();
    const calcState = location.state?.calculator || null;
    const prefill = location.state?.prefill || {};

    const [form, setForm] = useState({
        project_name: '',
        company: '',
        contact_name: '',
        email: '',
        phone: '',
        mat_type: prefill.mat_type || 'access',
        estimated_qty:
            prefill.estimated_qty != null ? String(prefill.estimated_qty) : '',
        project_type: prefill.project_type || 'access_road',
        province: 'BC',
        start_date: '',
        duration_weeks: '',
        notes: '',
        website: '', // honeypot
    });

    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    // Tracks whether the user has manually edited the mat-type / qty fields.
    // Live calculator prefill stops nudging them once the user takes over.
    const [userEdited, setUserEdited] = useState({
        mat_type: false, estimated_qty: false, project_type: false,
    });

    const update = (k) => (e) => {
        if (k in userEdited) setUserEdited((u) => ({ ...u, [k]: true }));
        setForm((f) => ({ ...f, [k]: e.target.value }));
    };

    // Live prefill from the in-page calculator (memoized so the callback
    // identity is stable between renders - prevents a useEffect loop in
    // MattingCalculator's onCalcChange broadcast).
    const onCalcChange = useCallback(
        ({ mat_type, total_mats, project_type, calc_state }) => {
            setForm((f) => ({
                ...f,
                mat_type: userEdited.mat_type ? f.mat_type : (mat_type || 'access'),
                estimated_qty: userEdited.estimated_qty
                    ? f.estimated_qty
                    : (total_mats != null ? String(total_mats) : ''),
                project_type: userEdited.project_type ? f.project_type : (project_type || 'access_road'),
                _calculator: calc_state,
            }));
        },
        [userEdited.mat_type, userEdited.estimated_qty, userEdited.project_type]
    );

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setError('');
        try {
            const payload = { ...form };
            // Coerce numeric fields
            payload.estimated_qty = payload.estimated_qty
                ? parseInt(payload.estimated_qty, 10)
                : null;
            payload.duration_weeks = payload.duration_weeks
                ? parseInt(payload.duration_weeks, 10)
                : null;
            // Drop empty optional strings
            ['start_date', 'notes'].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            // Prefer the live in-page calculator state when present, otherwise
            // fall back to state forwarded from the home calculator CTA.
            const liveCalc = payload._calculator;
            delete payload._calculator;
            if (liveCalc) payload.calculator = liveCalc;
            else if (calcState) payload.calculator = calcState;

            await submitQuote(payload);
            setStatus('success');
        } catch (err) {
            setStatus('error');
            const detail = err?.response?.data?.detail;
            setError(
                Array.isArray(detail)
                    ? detail.map((d) => `${d.loc?.join('.')}: ${d.msg}`).join('; ')
                    : typeof detail === 'string'
                      ? detail
                      : 'Submission failed. Please verify the highlighted fields and try again.'
            );
        }
    };

    return (
        <>
            <SEO
                title="Request a Manufacturer Direct Quote"
                description="Get a Manufacturer Direct quote for Centurion access, rig or crane mats. Pricing and lead times direct from our BC mills - no resellers."
                cmsSlug="seo.quote"
                path="/quote"
                image={HERO_MEDIA.muskeg.src}
                jsonLd={[
                    orgJsonLd(),
                    breadcrumbJsonLd([
                        { name: 'Home', path: '/' },
                        { name: 'Quote', path: '/quote' },
                    ]),
                ]}
            />

            <HeroPlaceholder
                mediaKey="muskeg"
                imageUrl={hero.image_url}
                eyebrow={hero.eyebrow}
                title={hero.headline}
                subtitle={hero.body}
                height="h-[360px] md:h-[420px]"
            />

            <section className="mx-auto max-w-7xl px-6 py-20 md:px-10 md:py-28">
                <span className="eyebrow">Quote Request</span>
                <h2 className="h-display mt-6 text-3xl sm:text-4xl">
                    Run the numbers. Submit the request.
                </h2>
                <p className="mt-5 max-w-3xl font-sans text-base text-brand-muted">
                    A Centurion sales lead will respond within 1 business day with
                    Manufacturer Direct pricing and lead time.
                </p>

                <div className="mt-12 grid gap-10 lg:grid-cols-5 lg:gap-10">
                    {/* LEFT - calculator (60% at lg+) */}
                    <div className="lg:col-span-3" data-testid="quote-calc-col">
                        <MattingCalculator
                            variant="full"
                            hideCta
                            onCalcChange={onCalcChange}
                            skipAnchorId="quote-form-anchor"
                        />
                    </div>

                    {/* RIGHT - quote form (40% at lg+) */}
                    <div className="lg:col-span-2" id="quote-form-anchor" data-testid="quote-form-col">
                        <p
                            data-testid="quote-form-auto-note"
                            className="font-sans text-xs leading-relaxed text-brand-muted"
                        >
                            Numbers update automatically from the estimator.
                            Edit any field to override.
                        </p>

                        {calcState ? (
                            <div
                                className="mt-6 border border-brand-gold bg-brand-surface p-5"
                                data-testid="quote-calc-summary"
                            >
                                <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                                    From Field Calculator
                                </span>
                                <p className="mt-2 font-sans text-sm text-brand-text">
                                    {`Access route - ${calcState.km} km, ${calcState.corners} corners → ${calcState.mats} mats`}
                                </p>
                            </div>
                        ) : null}

                {status === 'success' ? (
                    <div
                        data-testid="quote-success"
                        className="mt-10 flex items-start gap-4 border border-brand-gold bg-brand-surface p-6"
                    >
                        <CheckCircle2 className="mt-1 flex-none text-brand-gold" size={22} />
                        <div>
                            <p className="font-display text-base font-bold uppercase tracking-tight text-brand-text">
                                Thanks. Quote request received.
                            </p>
                            <p className="mt-2 font-sans text-sm text-brand-muted">
                                A Centurion sales lead will respond within 1 business day.
                            </p>
                        </div>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        data-testid="quote-form"
                        className="mt-10 grid gap-5"
                        noValidate
                    >
                        <input
                            type="text"
                            name="website"
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={update('website')}
                            style={{
                                position: 'absolute',
                                left: '-10000px',
                                width: '1px',
                                height: '1px',
                            }}
                            aria-hidden="true"
                        />

                        <FormRow label="Project name" required>
                            <Input value={form.project_name} onChange={update('project_name')} required testId="quote-input-project_name" />
                        </FormRow>
                        <div className="grid gap-5 md:grid-cols-2">
                            <FormRow label="Company" required>
                                <Input value={form.company} onChange={update('company')} required testId="quote-input-company" />
                            </FormRow>
                            <FormRow label="Contact name" required>
                                <Input value={form.contact_name} onChange={update('contact_name')} required testId="quote-input-contact_name" />
                            </FormRow>
                        </div>
                        <div className="grid gap-5 md:grid-cols-2">
                            <FormRow label="Email" required>
                                <Input type="email" value={form.email} onChange={update('email')} required testId="quote-input-email" />
                            </FormRow>
                            <FormRow label="Phone" required>
                                <Input type="tel" value={form.phone} onChange={update('phone')} required testId="quote-input-phone" />
                            </FormRow>
                        </div>

                        <div className="grid gap-5 md:grid-cols-2">
                            <FormRow label="Mat type" required>
                                <Select value={form.mat_type} onChange={update('mat_type')} testId="quote-input-mat_type">
                                    {MAT_TYPES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                                </Select>
                            </FormRow>
                            <FormRow label="Estimated quantity">
                                <Input
                                    type="number"
                                    min="0"
                                    value={form.estimated_qty}
                                    onChange={update('estimated_qty')}
                                    testId="quote-input-estimated_qty"
                                />
                            </FormRow>
                        </div>

                        <div className="grid gap-5 md:grid-cols-2">
                            <FormRow label="Project type" required>
                                <Select value={form.project_type} onChange={update('project_type')} testId="quote-input-project_type">
                                    {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </Select>
                            </FormRow>
                            <FormRow label="Project location" required>
                                <Select value={form.province} onChange={update('province')} testId="quote-input-province">
                                    {PROVINCES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                </Select>
                            </FormRow>
                        </div>

                        <div className="grid gap-5 md:grid-cols-2">
                            <FormRow label="Start date">
                                <Input type="date" value={form.start_date} onChange={update('start_date')} testId="quote-input-start_date" />
                            </FormRow>
                            <FormRow label="Duration (weeks)">
                                <Input type="number" min="0" value={form.duration_weeks} onChange={update('duration_weeks')} testId="quote-input-duration_weeks" />
                            </FormRow>
                        </div>

                        <FormRow label="Notes">
                            <Textarea value={form.notes} onChange={update('notes')} testId="quote-input-notes" />
                        </FormRow>

                        {error ? (
                            <p className="font-sans text-sm text-brand-danger" data-testid="quote-error">
                                {error}
                            </p>
                        ) : null}

                        <button
                            type="submit"
                            data-testid="quote-submit"
                            disabled={status === 'submitting'}
                            className="mt-2 inline-flex items-center justify-center gap-2 bg-brand-gold px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.16em] text-brand-base transition-colors duration-150 hover:bg-[#E5B647] disabled:opacity-60"
                        >
                            {status === 'submitting' ? (
                                <>
                                    <Loader2 className="animate-spin" size={14} /> Submitting…
                                </>
                            ) : (
                                'Submit Quote Request'
                            )}
                        </button>
                        <p
                            data-testid="quote-reassurance"
                            className="mt-4 font-sans text-xs leading-relaxed text-brand-muted"
                        >
                            Manufacturer Direct from our Chemainus, BC mill - quotes
                            returned within one business day.
                        </p>
                    </form>
                )}
                    </div>
                </div>
            </section>
        </>
    );
}

const FormRow = ({ label, required, children }) => (
    <label className="block">
        <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em] text-brand-muted">
            {label}
            {required ? <span className="text-brand-gold"> *</span> : null}
        </span>
        <div className="mt-2">{children}</div>
    </label>
);

const Input = ({ testId, ...props }) => (
    <input
        data-testid={testId}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    />
);

const Select = ({ testId, children, ...props }) => (
    <select
        data-testid={testId}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    >
        {children}
    </select>
);

const Textarea = ({ testId, ...props }) => (
    <textarea
        data-testid={testId}
        rows={4}
        {...props}
        className="w-full border border-brand-hairline bg-brand-elevated px-4 py-3 font-sans text-base text-brand-text outline-none transition-colors focus:border-brand-gold"
    />
);
